import { Box, Flex, IconButton } from '@chakra-ui/react';
import styled from '@emotion/styled';
import BEIcon from '@molecules/BEIcon';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Dots from '../Dots';
import { useBoundingRect } from '../hooks';
import { SliderProps } from './SliderProps';

const LeftIcon = styled(IconButton)`
  position: absolute;
  left: 8px;
  top: calc(50% - 20px);
  width: 40px;
  height: 40px;
  color: black;
  opacity: 0.7;
`;

const RightIcon = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: calc(50% - 20px);
  width: 40px;
  height: 40px;
  color: black;
  opacity: 0.7;
`;

const Slider = ({
  setTrackIsActive,
  initSliderWidth,
  initSliderHeight,
  setActiveItem,
  activeItem,
  constraint,
  direction,
  positions,
  gap,
  children,
  showDots,
}: SliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  const [ref, dimension] = useBoundingRect();
  const width = dimension?.width;
  const height = dimension?.height;

  const [isMouseIn, setMouseIn] = useState(false);

  useLayoutEffect(
    () => initSliderWidth(Math.round(width || 0)),
    [width, initSliderWidth]
  );

  useLayoutEffect(
    () => initSliderHeight(Math.round(height || 0)),
    [height, initSliderHeight]
  );

  const handleFocus = () => setTrackIsActive(true);

  const handleDecrementClick = () => {
    setTrackIsActive(true);
    !(activeItem === positions.length - positions.length) &&
      setActiveItem((prev) => prev - 1);
  };

  const handleIncrementClick = () => {
    setTrackIsActive(true);
    !(activeItem === positions.length - constraint) &&
      setActiveItem((prev) => prev + 1);
  };

  useEffect(() => {
    const handleMouseEnter = () => {
      setMouseIn(true);
    };

    const handleMouseLeave = () => {
      setMouseIn(false);
    };

    sliderRef.current?.addEventListener('mouseenter', handleMouseEnter);
    sliderRef.current?.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      sliderRef.current?.removeEventListener('mouseenter', handleMouseEnter);
      sliderRef.current?.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [sliderRef]);

  const showArrows =
    direction === 'horizontal' && isMouseIn && positions.length >= 2;
  const numDotsToShow =
    positions.length >= 5 ? 5 : positions.length === 1 ? 0 : positions.length;

  return (
    <Flex position="relative" flex={1} overflow="hidden" ref={sliderRef}>
      <Box
        ref={ref}
        w={
          direction === 'horizontal'
            ? { base: '100%', md: `calc(100% + ${gap}px)` }
            : '100%'
        }
        h={
          direction === 'horizontal'
            ? '100%'
            : { base: '100%', md: `calc(100% + ${gap}px)` }
        }
        {...(direction === 'horizontal'
          ? {
              ml: { base: 0, md: `-${gap / 2}px` },
              px: `${gap / 2}px`,
            }
          : {
              mt: { base: 0, md: `-${gap / 2}px` },
              py: `${gap / 2}px`,
            })}
        position="relative"
        overflow="hidden"
      >
        {children}
      </Box>
      {direction === 'horizontal' && positions.length > 1 && showDots && (
        <Dots
          numDots={positions.length}
          numDotsToShow={numDotsToShow}
          activeIndex={activeItem}
        />
      )}
      {showArrows && (
        <>
          <LeftIcon
            aria-label="prev"
            shadow="md"
            variant="solid"
            borderRadius="full"
            icon={<BEIcon name="arrow-left" size="lg" weight="solid" />}
            onClick={handleDecrementClick}
            onFocus={handleFocus}
          />
          <RightIcon
            aria-label="prev"
            shadow="md"
            variant="solid"
            borderRadius="full"
            icon={<BEIcon name="arrow-right" size="lg" weight="solid" />}
            onClick={handleIncrementClick}
            onFocus={handleFocus}
          />
        </>
      )}
    </Flex>
  );
};

export default Slider;
